import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/constants";
import { useAdminAuth, getAdminHeaders, formatDate } from "@/lib/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminSitePhotosPage({ language }) {
  const navigate = useNavigate();
  const authenticated = useAdminAuth(navigate);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!authenticated) return;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/site-photos`);
        setPhotos(res.data || []);
      } catch (err) {
        console.error("Failed to load site photos:", err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [authenticated]);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      formData.append("caption", caption);
      const res = await axios.post(`${API}/admin/site-photos`, formData, {
        headers: { ...getAdminHeaders(), "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [res.data, ...prev]);
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      alert("Failed to upload photo.");
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/site-photos/${id}`, { headers: getAdminHeaders() });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert("Failed to delete.");
    }
    setDeletingId(null);
  };

  if (!authenticated) return null;

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <Header language={language} />
      <main className="pt-28 pb-20">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/admin-dashboard"
            className="text-xs uppercase tracking-widest text-[#c2873c] hover:text-[#003366] transition-colors"
            data-testid="admin-photos-back"
          >
            &larr; Back to Dashboard
          </Link>
          <h1
            className="font-heading text-3xl sm:text-4xl text-[#003366] mt-3"
            data-testid="admin-photos-title"
          >
            Site Photos
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Upload project site photographs. These appear on the homepage as a rotating gallery.
          </p>

          <div
            className="mt-8 bg-white border border-slate-200 shadow-sm p-6"
            data-testid="admin-photos-upload"
          >
            <h3 className="font-heading text-xl text-[#003366]">Upload New Photo</h3>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="text-sm file:mr-3 file:px-4 file:py-2 file:border-0 file:bg-[#003366] file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider file:cursor-pointer"
                data-testid="admin-photos-file-input"
              />
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption (optional)"
                className="rounded-none max-w-xs"
                data-testid="admin-photos-caption-input"
              />
              <Button
                className="rounded-none bg-[#003366] text-white hover:bg-[#002244] px-6 text-xs font-bold uppercase tracking-wider"
                onClick={handleUpload}
                disabled={uploading}
                data-testid="admin-photos-upload-btn"
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>

          <div className="mt-8" data-testid="admin-photos-grid">
            {isLoading ? (
              <p className="text-slate-500 p-6 bg-white border border-slate-200">
                Loading photos...
              </p>
            ) : photos.length === 0 ? (
              <p
                className="text-slate-500 p-6 bg-white border border-slate-200"
                data-testid="admin-photos-empty"
              >
                No photos uploaded yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="bg-white border border-slate-200 shadow-sm overflow-hidden"
                    data-testid={`admin-photo-card-${index}`}
                  >
                    <div className="relative" style={{ paddingBottom: "66%" }}>
                      <img
                        loading="lazy"
                        src={`${API}/site-photos/${photo.id}/file`}
                        alt={photo.caption || `Site photo ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <span className="absolute top-2 left-2 w-7 h-7 bg-[#003366] text-white text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700 font-medium">
                          {photo.caption || "No caption"}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDate(photo.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-none px-3 py-1 text-xs font-bold uppercase tracking-wider"
                        onClick={() => handleDelete(photo.id)}
                        disabled={deletingId === photo.id}
                        data-testid={`admin-photo-delete-${index}`}
                      >
                        {deletingId === photo.id ? "..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
